// What the orders page says after a send to QuickBooks, from the `results[]` of
// POST /api/quickbooks/create-invoices: which orders were not sent and why, which sent
// invoices carry a placeholder price, and which lines went out on a linked QuickBooks
// item named like another product of the org.

/**
 * A line with no price anywhere (order line, this customer's invoice history,
 * the QuickBooks item) is invoiced at a placeholder and reported with
 * `priceSource: "default"` (server/quickbooks.ts). Tenants who price in QuickBooks
 * then set the real price on the draft — this list is what to look for there.
 */
const PLACEHOLDER_SOURCE = "default";

export interface NotSentOrder {
  orderId: number;
  customerName?: string;
  reason: string;
}

export interface PlaceholderPricedOrder {
  orderId: number;
  customerName?: string;
  invoiceNumber?: string;
  products: string[];
}

/**
 * A product linked to a QuickBooks item (Settings → QuickBooks, or the product dialog) is
 * invoiced against that item whatever its name. When the item is named like ANOTHER
 * product of the org the line carries `linkedItemName` (server/quickbooks.ts): that is how
 * a case went out as the each item (Pitt, 2026-10-26). Any other differently named item is
 * the tenant's choice and is never reported (Gomes' "Beefsteak", 2026-10-27). The send is
 * never refused for it — this list is what to check.
 */
export interface LinkedItemPair {
  productName: string;
  qbItemName: string;
}

export interface LinkedItemMismatchOrder {
  orderId: number;
  customerName?: string;
  invoiceNumber?: string;
  pairs: LinkedItemPair[];
}

export interface QuickbooksSendReport {
  notSent: NotSentOrder[];
  placeholderPriced: PlaceholderPricedOrder[];
  linkedItemMismatch: LinkedItemMismatchOrder[];
}

export function summarizeQuickbooksSend(results: unknown): QuickbooksSendReport {
  const report: QuickbooksSendReport = { notSent: [], placeholderPriced: [], linkedItemMismatch: [] };
  if (!Array.isArray(results)) return report;

  for (const result of results) {
    if (!result || typeof result !== "object") continue;
    const { orderId, customerName, invoiceNumber, success, error, lines } = result as Record<string, any>;

    if (!success) {
      report.notSent.push({ orderId, customerName, reason: error || "Invoice creation failed" });
      continue;
    }

    const sentLines = Array.isArray(lines) ? lines : [];
    const products = sentLines
      .filter((line) => line?.priceSource === PLACEHOLDER_SOURCE)
      .map((line) => String(line.productName));
    if (products.length > 0) {
      report.placeholderPriced.push({ orderId, customerName, invoiceNumber: invoiceNumber || undefined, products });
    }

    const pairs = sentLines
      .filter((line) => typeof line?.linkedItemName === "string" && line.linkedItemName !== "")
      .map((line) => ({ productName: String(line.productName), qbItemName: String(line.linkedItemName) }));
    if (pairs.length > 0) {
      report.linkedItemMismatch.push({ orderId, customerName, invoiceNumber: invoiceNumber || undefined, pairs });
    }
  }

  return report;
}
